// Purpose: connect SIP trunks to Console and run the guarded registration and
// LiveKit preview/apply steps.
//
// The connect form is deliberately carrier-agnostic: a trunk is a gateway
// host/port plus a transport and direction. Credentials are optional and decide
// the auth mode, so operators never pick one. The gateway address also creates
// the trunk's edge, because the peer that exchanges SIP with Console is exactly
// what LiveKit must allowlist.
//
// Infrastructure details Console already knows, such as the LiveKit SIP URI and
// the edge's GCP location, stay out of the primary form.

import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Plus } from 'lucide-react';

import {
  applySipTrunkLiveKit,
  applySipTrunkRegistration,
  connectSipTrunk,
  ConsoleApiError,
  fetchSipTrunks,
  isUnauthorizedApiError,
  previewSipTrunkLiveKit,
  previewSipTrunkRegistration,
  setSipTrunkEdges,
} from './apiClient';
import type {
  ApiSipTrunk,
  ApiSipTrunkApplyJob,
  ApiSipTrunkEdge,
  ApiSipTrunkLiveKitPreview,
  ApiSipTrunkRegistrationPreview,
  ConnectSipTrunkInput,
} from './apiClient';
import { EmptyState, MetricCard, StatusPill } from './uiPrimitives';

type EdgeRole = ApiSipTrunkEdge['role'];

const EMPTY_EDGE: ApiSipTrunkEdge = {
  edgeId: '',
  edgeProjectId: '',
  edgeZone: '',
  edgePublicIp: '',
  role: 'active',
};

function PreviewPanel(props: { title: string; items?: string[]; blockers?: string[] }) {
  if (!props.items) return null;
  return (
    <div className="side-stack">
      <h3>{props.title}</h3>
      {props.blockers?.length ? <p className="api-banner warning">Blockers: {props.blockers.join(', ')}</p> : null}
      <ul>{props.items.map((item) => <li key={item}>{item}</li>)}</ul>
    </div>
  );
}


function provisioningExplanation(status: ApiSipTrunk['provisioningStatus']): string {
  if (status === 'ready') return 'LiveKit is in sync with Console.';
  if (status === 'error') return 'The last apply failed. See apply jobs below.';
  return 'Waiting for Console to sync this trunk to LiveKit.';
}

function healthLabel(status: ApiSipTrunk['healthStatus']): string {
  if (status === 'registered') return 'Healthy';
  if (status === 'unregistered') return 'Not ready';
  if (status === 'failing') return 'Failing';
  return 'Not checked yet';
}

function healthExplanation(trunk: ApiSipTrunk): string {
  if (trunk.healthStatus === 'registered') {
    return trunk.authMode === 'registration'
      ? 'LiveKit is configured and the edge is registered with the carrier.'
      : 'LiveKit is configured and allows this trunk\u2019s edge.';
  }
  if (trunk.lastHealthCheckedAt) return 'Checked, but not ready to carry calls.';
  return 'Console has not checked this trunk yet.';
}

function ApplyJobRow(props: { job: ApiSipTrunkApplyJob }) {
  const { job } = props;
  const target = {
    livekit: 'LiveKit sync',
    livekit_outbound: 'Outgoing calls',
    registration: 'Edge registration',
  }[job.target];
  const retryAt = job.nextAttemptAt ? new Date(job.nextAttemptAt).toLocaleTimeString() : null;
  return (
    <div className="record-row-card" key={job.applyJobId}>
      <div><span className="table-label">Step</span><strong>{target}</strong><span>{job.status}</span></div>
      <div><span className="table-label">Attempts</span><span className="mono">{job.attemptCount} of {job.maxAttempts}</span></div>
      <div>
        <span className="table-label">Detail</span>
        {job.failureSummary ? <span>{job.failureSummary}</span> : <span>{job.status === 'succeeded' ? 'Completed.' : 'In progress.'}</span>}
        {job.failureNextAction ? <small className="compact-copy">{job.failureNextAction}</small> : null}
        {job.willRetry && retryAt ? <small className="compact-copy">Next attempt around {retryAt}.</small> : null}
      </div>
    </div>
  );
}

export function SipTrunksScreen({ onSessionExpired }: { onSessionExpired: () => void }) {
  const [trunks, setTrunks] = useState<ApiSipTrunk[]>([]);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [message, setMessage] = useState<string | null>(null);
  const [selectedTrunkId, setSelectedTrunkId] = useState<string | null>(null);
  const [registrationPreview, setRegistrationPreview] = useState<ApiSipTrunkRegistrationPreview | null>(null);
  const [livekitPreview, setLivekitPreview] = useState<ApiSipTrunkLiveKitPreview | null>(null);
  const [applyJobs, setApplyJobs] = useState<ApiSipTrunkApplyJob[]>([]);
  const [edgeForm, setEdgeForm] = useState<ApiSipTrunkEdge>(EMPTY_EDGE);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [form, setForm] = useState<ConnectSipTrunkInput>({
    name: '',
    gatewayHost: '',
    gatewayPort: 5060,
    transport: 'udp',
    direction: 'bidirectional',
    sipUsername: '',
    sipPassword: '',
  });

  const selectedTrunk = trunks.find((trunk) => trunk.sipTrunkId === selectedTrunkId) ?? trunks[0];
  const selectedTrunkEdges = selectedTrunk?.edges ?? [];

  // Load once on mount. loadState already starts as 'loading', so the effect
  // must not set it synchronously.
  useEffect(() => {
    let cancelled = false;
    fetchSipTrunks()
      .then((loaded) => {
        if (cancelled) return;
        setTrunks(loaded);
        setSelectedTrunkId((current) => current ?? loaded[0]?.sipTrunkId ?? null);
        setLoadState('ready');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (isUnauthorizedApiError(error)) {
          onSessionExpired();
          return;
        }
        setLoadState('error');
        setMessage(error instanceof ConsoleApiError ? error.message : 'SIP trunk connections failed to load');
      });
    return () => {
      cancelled = true;
    };
  }, [onSessionExpired]);

  async function handleConnect(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage('Connecting SIP trunk\u2026');
    try {
      // Send credentials only when both are present; the API derives the auth
      // mode from their presence.
      const hasCredentials = Boolean(form.sipUsername?.trim() && form.sipPassword);
      const connected = await connectSipTrunk({
        name: form.name,
        gatewayHost: form.gatewayHost.trim(),
        gatewayPort: form.gatewayPort,
        transport: form.transport,
        direction: form.direction,
        ...(hasCredentials
          ? { sipUsername: form.sipUsername?.trim(), sipPassword: form.sipPassword }
          : {}),
      });
      setTrunks((current) => [connected, ...current.filter((trunk) => trunk.sipTrunkId !== connected.sipTrunkId)]);
      setSelectedTrunkId(connected.sipTrunkId);
      setRegistrationPreview(null);
      setLivekitPreview(null);
      setMessage('Trunk connected. Console is syncing it to LiveKit automatically; this usually takes under a minute.');
      setForm((current) => ({ ...current, sipPassword: '' }));
    } catch (error) {
      if (isUnauthorizedApiError(error)) {
        onSessionExpired();
        return;
      }
      setMessage(error instanceof ConsoleApiError ? error.message : 'Could not connect SIP trunk');
    }
  }

  async function handleAttachEdge(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedTrunk) return;
    const existing = selectedTrunk.edges ?? [];
    if (existing.some((edge) => edge.edgeId === edgeForm.edgeId)) {
      setMessage(`Edge ${edgeForm.edgeId} is already attached to this trunk.`);
      return;
    }
    // Only one edge may be active, so demote any current active edge.
    const edges = edgeForm.role === 'active'
      ? [...existing.map((edge) => (edge.role === 'active' ? { ...edge, role: 'backup' as EdgeRole } : edge)), edgeForm]
      : [...existing, edgeForm];

    setMessage(`Attaching edge ${edgeForm.edgeId}…`);
    try {
      const updated = await setSipTrunkEdges(selectedTrunk.sipTrunkId, edges);
      setTrunks((current) => current.map((item) => (item.sipTrunkId === updated.sipTrunkId ? updated : item)));
      setRegistrationPreview(null);
      setLivekitPreview(null);
      setEdgeForm(EMPTY_EDGE);
      setMessage('Edge attached. Run previews again before applying changes.');
    } catch (error) {
      if (isUnauthorizedApiError(error)) {
        onSessionExpired();
        return;
      }
      setMessage(error instanceof ConsoleApiError ? error.message : 'Edge attach failed');
    }
  }

  async function handleEdgeRoleChange(trunk: ApiSipTrunk, edgeId: string, role: EdgeRole) {
    const edges = (trunk.edges ?? []).map((edge) => ({
      ...edge,
      role: edge.edgeId === edgeId ? role : role === 'active' && edge.role === 'active' ? ('backup' as EdgeRole) : edge.role,
    }));
    setMessage(`Updating edge roles for ${trunk.name}…`);
    try {
      const updated = await setSipTrunkEdges(trunk.sipTrunkId, edges);
      setTrunks((current) => current.map((item) => (item.sipTrunkId === updated.sipTrunkId ? updated : item)));
      setRegistrationPreview(null);
      setLivekitPreview(null);
      setMessage('Edge roles updated. Run previews again before applying changes.');
    } catch (error) {
      if (isUnauthorizedApiError(error)) {
        onSessionExpired();
        return;
      }
      setMessage(error instanceof ConsoleApiError ? error.message : 'Edge role update failed');
    }
  }

  async function runRegistrationPreview(trunk: ApiSipTrunk) {
    setMessage('Building registration preview…');
    try {
      const preview = await previewSipTrunkRegistration(trunk.sipTrunkId);
      setRegistrationPreview(preview);
      setMessage(preview.blockers.length ? 'Registration preview has blockers.' : 'Registration preview is ready.');
    } catch (error) {
      if (isUnauthorizedApiError(error)) {
        onSessionExpired();
        return;
      }
      setMessage(error instanceof ConsoleApiError ? error.message : 'Registration preview failed');
    }
  }

  async function runLiveKitPreview(trunk: ApiSipTrunk) {
    setMessage('Building LiveKit preview…');
    try {
      const preview = await previewSipTrunkLiveKit(trunk.sipTrunkId);
      setLivekitPreview(preview);
      setMessage(preview.unsafe.length ? 'LiveKit preview has unsafe blockers.' : 'LiveKit preview is ready.');
    } catch (error) {
      if (isUnauthorizedApiError(error)) {
        onSessionExpired();
        return;
      }
      setMessage(error instanceof ConsoleApiError ? error.message : 'LiveKit preview failed');
    }
  }

  async function runRegistrationApply(trunk: ApiSipTrunk) {
    if (!registrationPreview || registrationPreview.blockers.length > 0) return;
    setMessage('Queueing registration apply job…');
    try {
      const job = await applySipTrunkRegistration(trunk.sipTrunkId, registrationPreview.planHash);
      setApplyJobs((current) => [job, ...current.filter((item) => item.applyJobId !== job.applyJobId)]);
      setMessage(`Registration apply job ${job.status}.`);
    } catch (error) {
      if (isUnauthorizedApiError(error)) {
        onSessionExpired();
        return;
      }
      setMessage(error instanceof ConsoleApiError ? error.message : 'Registration apply failed');
    }
  }

  async function runLiveKitApply(trunk: ApiSipTrunk) {
    if (!livekitPreview || livekitPreview.unsafe.length > 0) return;
    setMessage('Queueing LiveKit apply job…');
    try {
      const job = await applySipTrunkLiveKit(trunk.sipTrunkId, livekitPreview.planHash);
      setApplyJobs((current) => [job, ...current.filter((item) => item.applyJobId !== job.applyJobId)]);
      setMessage(`LiveKit apply job ${job.status}.`);
    } catch (error) {
      if (isUnauthorizedApiError(error)) {
        onSessionExpired();
        return;
      }
      setMessage(error instanceof ConsoleApiError ? error.message : 'LiveKit apply failed');
    }
  }

  return (
    <section className="screen" aria-labelledby="sip-trunks-heading">
      <header className="screen-header">
        <div>
          <h1 id="sip-trunks-heading">SIP Trunks</h1>
          <p>Add a SIP trunk from any carrier. Console needs the gateway address it exchanges SIP with, plus credentials only if your carrier requires them.</p>
          <p className="compact-copy">A new trunk is synced to LiveKit automatically. Later changes need an explicit preview and apply, because they can remove numbers or addresses from a trunk that is already carrying calls. Failed applies retry on their own with backoff.</p>
          {message ? <p className="compact-copy" role="status">{message}</p> : null}
        </div>
      </header>
      <div className="trunk-grid">
        <section className="panel" aria-labelledby="connect-trunk-heading">
          <h2 id="connect-trunk-heading">Add SIP trunk</h2>
          <form className="form-stack" onSubmit={(event) => void handleConnect(event)}>
            <label className="auth-field"><span>Name</span><input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="prod-trunk" required /></label>

            <div className="field-stack">
              <label className="auth-field"><span>IP address or domain</span><input value={form.gatewayHost} onChange={(event) => setForm({ ...form, gatewayHost: event.target.value })} placeholder="34.151.90.132" required /></label>
              <small className="compact-copy">The gateway Console exchanges SIP with. An IP address is registered as this trunk's edge automatically.</small>
            </div>

            <div className="form-grid-two">
              <label className="auth-field"><span>Port</span><input type="number" min="1" max="65535" value={form.gatewayPort} onChange={(event) => setForm({ ...form, gatewayPort: Number(event.target.value) })} required /></label>
              <label className="auth-field">
                <span>Protocol</span>
                <select className="compact-select" value={form.transport} onChange={(event) => setForm({ ...form, transport: event.target.value as ConnectSipTrunkInput['transport'] })}>
                  <option value="udp">UDP</option>
                  <option value="tcp">TCP</option>
                  <option value="tls">TLS</option>
                </select>
              </label>
            </div>

            <label className="auth-field">
              <span>Direction</span>
              <select className="compact-select" value={form.direction} onChange={(event) => setForm({ ...form, direction: event.target.value as ConnectSipTrunkInput['direction'] })}>
                <option value="bidirectional">Inbound &amp; Outbound</option>
                <option value="inbound">Inbound only</option>
                <option value="outbound">Outbound only</option>
              </select>
            </label>

            <div className="field-stack">
              <div className="form-grid-two">
                <label className="auth-field"><span>Username</span><input value={form.sipUsername ?? ''} onChange={(event) => setForm({ ...form, sipUsername: event.target.value })} autoComplete="off" /></label>
                <label className="auth-field"><span>Password</span><input type="password" value={form.sipPassword ?? ''} onChange={(event) => setForm({ ...form, sipPassword: event.target.value })} autoComplete="new-password" /></label>
              </div>
              <small className="compact-copy">Optional. Leave blank when your carrier authenticates Console by IP address. Passwords are stored once and never displayed again.</small>
            </div>

            <button className="primary-button" type="submit"><Plus size={16} aria-hidden="true" /> Add trunk</button>
          </form>
        </section>
        <section className="panel" aria-labelledby="trunk-list-heading">
          <h2 id="trunk-list-heading">Connected trunks</h2>
          {loadState === 'loading' ? <EmptyState message="Loading SIP trunk connections…" /> : null}
          {loadState === 'error' ? <EmptyState message="Could not load SIP trunk connections." /> : null}
          {loadState === 'ready' && trunks.length === 0 ? <EmptyState message="No SIP trunks connected yet." /> : null}
          <div className="activity-list">
            {trunks.map((trunk) => (
              <button className={`record-row-card ${selectedTrunk?.sipTrunkId === trunk.sipTrunkId ? 'selected-row' : ''}`} type="button" key={trunk.sipTrunkId} onClick={() => setSelectedTrunkId(trunk.sipTrunkId)}>
                <div><span className="table-label">Trunk</span><strong>{trunk.name}</strong><span className="mono">{trunk.gatewayHost}:{trunk.gatewayPort} · {trunk.transport.toUpperCase()}</span></div>
                <div><span className="table-label">Auth</span><span>{trunk.authMode === 'ip_allowlist' ? 'IP address' : 'Credentials'}</span></div>
                <div><span className="table-label">Direction</span><span>{trunk.direction === 'bidirectional' ? 'In & Out' : trunk.direction}</span></div>
                <div><span className="table-label">State</span><StatusPill status={trunk.provisioningStatus} /></div>
                <div><span className="table-label">Health</span><StatusPill status={trunk.healthStatus} /></div>
                <div><span className="table-label">DIDs</span><span>{trunk.didCount}</span></div>
              </button>
            ))}
          </div>
        </section>
      </div>
      {selectedTrunk ? (
        <section className="panel" aria-labelledby="trunk-detail-heading">
          <h2 id="trunk-detail-heading">Activation preview</h2>
          <div className="metric-grid">
            <MetricCard label="Status" value={selectedTrunk.status} detail={provisioningExplanation(selectedTrunk.provisioningStatus)} tone={selectedTrunk.provisioningStatus === 'error' ? 'warn' : 'good'} />
            <MetricCard label="LiveKit SIP URI" value={selectedTrunk.livekitSipUri ?? 'Missing'} detail="explicit SIP URI only" />
            <MetricCard label="Health" value={healthLabel(selectedTrunk.healthStatus)} detail={selectedTrunk.lastHealthError ?? healthExplanation(selectedTrunk)} tone={selectedTrunk.healthStatus === 'registered' ? 'good' : selectedTrunk.healthStatus === 'unknown' ? undefined : 'warn'} />
            <MetricCard label="Active edge" value={selectedTrunk.activeEdge?.edgeId ?? 'Not assigned'} detail={selectedTrunk.activeEdge?.edgePublicIp ?? 'edge assignment pending'} />
            <MetricCard
              label="Credential"
              value={selectedTrunk.authMode === 'ip_allowlist' ? 'Not required' : selectedTrunk.secretConfigured ? 'Stored' : 'Missing'}
              detail={selectedTrunk.authMode === 'ip_allowlist' ? 'IP allowlist trunk' : 'never returned by API'}
              tone={selectedTrunk.authMode === 'ip_allowlist' || selectedTrunk.secretConfigured ? 'good' : 'warn'}
            />
          </div>

          <div className="side-stack">
            <button className="secondary-button" type="button" aria-expanded={showAdvanced} onClick={() => setShowAdvanced((current) => !current)}>
              {showAdvanced ? 'Hide advanced' : 'Advanced: add a backup or managed edge'}
            </button>
            {showAdvanced ? (
              <>
                <p className="compact-copy">Adding a trunk by IP address already registers its edge. Use this to add a backup edge for failover, or to record the GCP location of an edge Console manages, which registration apply needs.</p>
                <form className="form-stack" onSubmit={(event) => void handleAttachEdge(event)}>
                  <div className="form-grid-two">
                    <label className="auth-field"><span>Edge ID</span><input value={edgeForm.edgeId} onChange={(event) => setEdgeForm({ ...edgeForm, edgeId: event.target.value })} placeholder="sip-edge-aatrox-vapi" required /></label>
                    <label className="auth-field"><span>Public IP</span><input value={edgeForm.edgePublicIp} onChange={(event) => setEdgeForm({ ...edgeForm, edgePublicIp: event.target.value })} placeholder="34.151.90.132" required /></label>
                  </div>
                  <div className="form-grid-two">
                    <label className="auth-field"><span>GCP project</span><input value={edgeForm.edgeProjectId ?? ''} onChange={(event) => setEdgeForm({ ...edgeForm, edgeProjectId: event.target.value })} placeholder="callable-agent-platform" /></label>
                    <label className="auth-field"><span>Zone</span><input value={edgeForm.edgeZone ?? ''} onChange={(event) => setEdgeForm({ ...edgeForm, edgeZone: event.target.value })} placeholder="australia-southeast1-a" /></label>
                  </div>
                  <label className="auth-field">
                    <span>Role</span>
                    <select className="compact-select" value={edgeForm.role} onChange={(event) => setEdgeForm({ ...edgeForm, role: event.target.value as EdgeRole })}>
                      <option value="active">Active</option>
                      <option value="backup">Backup</option>
                      <option value="disabled">Disabled</option>
                    </select>
                  </label>
                  <button className="secondary-button" type="submit"><Plus size={16} aria-hidden="true" /> Attach edge</button>
                </form>
              </>
            ) : null}
          </div>

          {selectedTrunkEdges.length ? (
            <div className="side-stack">
              <h3>Edge roles</h3>
              <p className="compact-copy">Choose which edge owns this trunk before previewing or applying.</p>
              <div className="activity-list">
                {selectedTrunkEdges.map((edge) => (
                  <div className="record-row-card" key={edge.edgeId}>
                    <div><span className="table-label">Edge</span><strong>{edge.edgeId}</strong><span className="mono">{edge.edgePublicIp}</span></div>
                    <div><span className="table-label">Zone</span><span>{edge.edgeZone}</span></div>
                    <label className="auth-field">
                      <span>Role</span>
                      <select className="compact-select" value={edge.role} onChange={(event) => void handleEdgeRoleChange(selectedTrunk, edge.edgeId, event.target.value as EdgeRole)}>
                        <option value="active">Active</option>
                        <option value="backup">Backup</option>
                        <option value="disabled">Disabled</option>
                      </select>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="api-banner warning">No edge is attached to this trunk yet. Add the trunk by IP address, or use Advanced to attach one.</div>
          )}

          <div className="button-row">
            <button className="secondary-button" type="button" onClick={() => void runRegistrationPreview(selectedTrunk)}>Preview edge registration</button>
            <button className="secondary-button" type="button" onClick={() => void runLiveKitPreview(selectedTrunk)}>Preview LiveKit sync</button>
            <button className="primary-button" type="button" disabled={!registrationPreview || registrationPreview.blockers.length > 0} onClick={() => void runRegistrationApply(selectedTrunk)}>Apply registration</button>
            <button className="primary-button" type="button" disabled={!livekitPreview || livekitPreview.unsafe.length > 0} onClick={() => void runLiveKitApply(selectedTrunk)}>Apply LiveKit</button>
          </div>
          <PreviewPanel title="Registration preview" items={registrationPreview?.actions} blockers={registrationPreview?.blockers} />
          <PreviewPanel title="LiveKit preview" items={livekitPreview?.changes} blockers={livekitPreview?.unsafe} />
          {applyJobs.length ? (
            <div className="side-stack">
              <h3>Apply jobs</h3>
              <div className="activity-list">
                {applyJobs.map((job) => <ApplyJobRow job={job} key={job.applyJobId} />)}
              </div>
            </div>
          ) : null}
        </section>
      ) : null}
    </section>
  );
}
